import { Rastgele, hasarHesapla, DENGE } from '@kurultay/kurallar'
import { DURUS_HASAR_TURU } from '@kurultay/tipler'
import type { HasarTuru } from '@kurultay/tipler'
import { gecerliHedefler } from './hedefleme.js'
import { durusDegistirilebilirMi, durusDegistir, turBasindaSifirla } from './durus.js'
import { BECERILER } from './beceri.js'
import type { Beceri } from './beceri.js'
import { yapayZekaKomutu, temelBeceri } from './komut.js'
import type { TurGirdisi, KahramanKomutu } from './komut.js'
import type { SavasDurumu, Kahraman, Taraf } from './tipler.js'

export type SavasOlayi =
  | { tip: 'savas_basladi'; tohum: number }
  | { tip: 'tur_basladi'; tur: number }
  | { tip: 'durus_degisti'; birimId: string; yeniDurus: string; safDegisti: boolean }
  | { tip: 'saldiri'; kaynakId: string; hedefId: string; beceriKodu: string
      hasar: number; hasarTuru: HasarTuru; kritik: boolean; isabetEtti: boolean }
  | { tip: 'ofke_degisti'; birimId: string; yeni: number }
  | { tip: 'olum'; birimId: string }
  | { tip: 'savas_bitti'; kazanan: Taraf | null; turSayisi: number }

/** Çerilerin temel saldırısı — beceri sistemine sahip değiller. */
const CERI_SALDIRISI: Beceri = {
  kod: 'ceri_saldirisi', ad: 'Çeri Saldırısı', durus: 'kilic',
  ofkeMaliyeti: 0, carpan: 1, safDeler: false, hedefSayisi: 1,
}

export function yasayanVarMi(durum: SavasDurumu, taraf: Taraf): boolean {
  return durum.birimler.some((b) => b.taraf === taraf && b.yasiyor)
}

/**
 * Komuttan beceri çözer. Geçersiz kod veya yetersiz öfke temel beceriye düşer —
 * böylece istemciden gelen bozuk komut savaşı kilitleyemez.
 */
function beceriCoz(k: Kahraman, komutlar: KahramanKomutu[]): Beceri {
  const komut = komutlar.find((c) => c.tip === 'beceri')
  if (!komut || komut.tip !== 'beceri') return temelBeceri(k)
  const beceri = BECERILER[komut.beceriKodu]
  if (!beceri || beceri.durus !== k.durus || beceri.ofkeMaliyeti > k.ofke) {
    return temelBeceri(k)
  }
  return beceri
}

/**
 * Tek turu işler. Durumu YERİNDE günceller, ürettiği olayları döner.
 * Kahraman kararları dışarıdan komut olarak gelir; komut verilmeyen
 * kahraman yapay zekâ ile oynar. Otomatik ve etkileşimli kip aynı çekirdeği kullanır.
 */
export function turuIsle(
  durum: SavasDurumu,
  girdi: TurGirdisi,
  r: Rastgele,
): SavasOlayi[] {
  const olaylar: SavasOlayi[] = [{ tip: 'tur_basladi', tur: durum.tur }]

  durum.birimler = durum.birimler.map((b) =>
    b.tur === 'kahraman' ? turBasindaSifirla(b) : b,
  )

  // İnisiyatif: hız + tohumlu kırıcı. Beraberlikte id ile kararlı sıralama.
  const sira = durum.birimler
    .filter((b) => b.yasiyor)
    .map((b) => ({ id: b.id, oncelik: b.hiz + r.tamsayi(0, 9) }))
    .sort((x, y) => y.oncelik - x.oncelik || x.id.localeCompare(y.id))
    .map((x) => x.id)

  for (const birimId of sira) {
    const indeks = durum.birimler.findIndex((b) => b.id === birimId)
    let birim = durum.birimler[indeks]
    if (!birim || !birim.yasiyor) continue

    let beceri = CERI_SALDIRISI
    if (birim.tur === 'kahraman') {
      // Boş komut listesi "sen karar ver" demektir, "hiçbir şey yapma" değil.
      // ?? kullanmak yanlıştı: boş dizi truthy olduğu için yapay zekâ hiç
      // çağrılmıyor ve kahraman 100 öfkeyle bile bedava vuruşta kalıyordu.
      const verilen = girdi.komutlar[birim.id]
      const komutlar = verilen && verilen.length > 0
        ? verilen
        : yapayZekaKomutu(durum, birim)

      if (komutlar.some((c) => c.tip === 'durus_degistir') && durusDegistirilebilirMi(birim)) {
        const onceSaf = birim.saf
        const yeni = durusDegistir(birim)
        durum.birimler[indeks] = yeni
        olaylar.push({
          tip: 'durus_degisti', birimId: yeni.id,
          yeniDurus: yeni.durus, safDegisti: onceSaf !== yeni.saf,
        })
        birim = yeni
      }
      beceri = beceriCoz(birim, komutlar)
    }

    const hasarTuru: HasarTuru =
      birim.tur === 'kahraman' ? DURUS_HASAR_TURU[birim.durus] : 'fiziksel'

    const hedefler = gecerliHedefler(durum, birim, beceri.safDeler).slice(0, beceri.hedefSayisi)
    if (hedefler.length === 0) continue

    if (birim.tur === 'kahraman' && beceri.ofkeMaliyeti > 0) {
      const kalan = birim.ofke - beceri.ofkeMaliyeti
      durum.birimler[indeks] = { ...birim, ofke: kalan }
      olaylar.push({ tip: 'ofke_degisti', birimId: birim.id, yeni: kalan })
    }

    const saldiran = durum.birimler[indeks]!
    for (const hedef of hedefler) {
      const hIndeks = durum.birimler.findIndex((b) => b.id === hedef.id)
      const h = durum.birimler[hIndeks]
      if (!h || !h.yasiyor) continue

      const sonuc = hasarHesapla(
        {
          saldiri: saldiran.saldiri, carpan: beceri.carpan, hasarTuru,
          kritikSansi: saldiran.kritikSansi, isabet: saldiran.isabet,
        },
        { zirh: h.zirh, ruhDirenci: h.ruhDirenci, kacinma: h.kacinma },
        r,
      )

      // Duruş cezası: saf değiştiren kahraman bu turdaki İLK hasarı artırılmış alır
      const cezali = h.tur === 'kahraman' && h.durusCezasiAktif && sonuc.isabetEtti
      const nihaiHasar = cezali
        ? Math.round(sonuc.hasar * DENGE.durusCezasiCarpani)
        : sonuc.hasar

      const yeniCan = Math.max(0, h.can - nihaiHasar)
      durum.birimler[hIndeks] = h.tur === 'kahraman'
        ? { ...h, can: yeniCan, yasiyor: yeniCan > 0, durusCezasiAktif: false }
        : { ...h, can: yeniCan, yasiyor: yeniCan > 0 }

      olaylar.push({
        tip: 'saldiri', kaynakId: saldiran.id, hedefId: h.id,
        beceriKodu: beceri.kod, hasar: nihaiHasar, hasarTuru,
        kritik: sonuc.kritik, isabetEtti: sonuc.isabetEtti,
      })

      if (yeniCan === 0) olaylar.push({ tip: 'olum', birimId: h.id })
    }
  }

  // Tur sonu öfke birikimi
  durum.birimler = durum.birimler.map((b) => {
    if (b.tur !== 'kahraman' || !b.yasiyor) return b
    const yeni = Math.min(DENGE.azamiOfke, b.ofke + DENGE.turBasinaOfke)
    if (yeni !== b.ofke) olaylar.push({ tip: 'ofke_degisti', birimId: b.id, yeni })
    return { ...b, ofke: yeni }
  })

  const aYasiyor = yasayanVarMi(durum, 'a')
  const bYasiyor = yasayanVarMi(durum, 'b')
  if (!aYasiyor || !bYasiyor) {
    durum.bitti = true
    durum.kazanan = aYasiyor ? 'a' : bYasiyor ? 'b' : null
  } else {
    durum.tur++
  }

  return olaylar
}
